import { type FormEvent, useId, useState } from 'react';

import {
  Button,
  Checkbox,
  Dialog,
  FormField,
  Input,
  Select,
} from '@revelio/ui';

import { FilterId } from '../../../filters/types/filters';
import type { ReportParameterConfig } from '../../PageConfigProvider/page-reducer';
import type { PlotParameter } from '../../PageConfigProvider/types/item';
import styles from './edit-parameter-modal.module.css';
import {
  defaultReportParameterFilters,
  filterCanBePrimary,
  toReadableFilterName,
} from './parameters';

/** What the Company filter was called before Industry was split out of it. */
const LEGACY_COMPANY_PARAMETER_NAME = 'Company & Industry';

export function EditParameterModal({
  isOpen,
  onClose,
  mode,
  initial,
  onSubmit,
  isChartLevel = false,
  plotParameter,
  onGroupingChange,
}: {
  isOpen: boolean;
  onClose: () => void;
  mode: 'add' | 'edit';
  initial: ReportParameterConfig;
  onSubmit: (param: ReportParameterConfig) => void;
  isChartLevel?: boolean;
  plotParameter?: PlotParameter;
  onGroupingChange?: (grouped: boolean) => void;
}) {
  // Chakra made the whole ModalContent the <form>; the dialog owns its own
  // chrome now, so the form lives in the body and the footer's Save submits it
  // by id — which keeps Enter-to-submit working from the fields.
  const formId = useId();
  const selectId = `${formId}-parameter`;
  const nameId = `${formId}-name`;
  const limitId = `${formId}-limit`;
  const [draft, setDraft] = useState<ReportParameterConfig>({
    ...initial,
    selectionLimit: initial.selectionLimit ?? 1,
  });

  const [groupingDraft, setGroupingDraft] = useState<boolean>(
    plotParameter?.grouped ?? true
  );

  const availableFilterIds = defaultReportParameterFilters;

  type FilterOption = {
    value: FilterId;
    label: string;
    isDisabled?: boolean;
  };

  const save = () => {
    onSubmit(draft);
    if (isChartLevel && onGroupingChange) {
      onGroupingChange(groupingDraft);
    }
    onClose();
  };

  const isDateRange = draft.filterId === FilterId.DATE_RANGE;

  const showGroupingUI =
    isChartLevel &&
    !initial.isPrimary &&
    !isDateRange &&
    (draft.selectionLimit ?? 1) > 1;

  const unitOfAnalysisOptions = availableFilterIds.filter(filterCanBePrimary);

  const options: FilterOption[] = [
    ...unitOfAnalysisOptions.map((id) => ({
      value: id,
      label: toReadableFilterName(id),
    })),
    {
      value: FilterId.DATE_RANGE,
      label: toReadableFilterName(FilterId.DATE_RANGE),
    },
  ];

  const selected: FilterOption | null =
    options.find((opt) => opt.value === draft.filterId) ?? null;

  const handleSelectChange = (opt: FilterOption | null) => {
    if (!opt) return;
    const nextFilterId = opt.value;
    setDraft((prev) => {
      const prevDefault = toReadableFilterName(prev.filterId);
      const nextDefault = toReadableFilterName(nextFilterId);
      // A parameter auto-named before Company and Industry were split still
      // carries the old combined label. Treat that as a default too, or
      // switching its filter leaves the stale name behind.
      const shouldUpdateName =
        !prev.name ||
        prev.name === prevDefault ||
        (prev.filterId === FilterId.COMPANY &&
          prev.name === LEGACY_COMPANY_PARAMETER_NAME);
      const nextIsDateRange = nextFilterId === FilterId.DATE_RANGE;
      return {
        ...prev,
        filterId: nextFilterId,
        name: shouldUpdateName ? nextDefault : prev.name,
        ...(nextIsDateRange
          ? { isPrimary: false, isOptional: true, selectionLimit: undefined }
          : { isOptional: false, selectionLimit: prev.selectionLimit ?? 1 }),
      };
    });
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      title={mode === 'add' ? 'Add Parameter' : 'Edit Parameter'}
      size="md"
      footer={
        <>
          <Button
            size="sm"
            color="secondary"
            variant="ghost"
            type="button"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button size="sm" color="primary" type="submit" form={formId}>
            Save
          </Button>
        </>
      }
    >
      <form
        id={formId}
        className={styles.form}
        onSubmit={(e: FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          save();
        }}
      >
        <FormField label="Parameters" htmlFor={selectId}>
          <div data-testid="parameter-select">
            <Select<FilterOption, false>
              inputId={selectId}
              options={options}
              value={selected}
              onChange={handleSelectChange}
              isSearchable
            />
          </div>
        </FormField>
        <FormField label="Name" htmlFor={nameId}>
          <Input
            id={nameId}
            value={draft.name}
            onChange={(e) =>
              setDraft({
                ...draft,
                name: e.target.value,
              })
            }
          />
        </FormField>
        {!isDateRange && (
          <FormField label="Selection Limit" htmlFor={limitId}>
            <Input
              id={limitId}
              type="number"
              min={1}
              value={draft.selectionLimit ?? 1}
              onChange={(e) => {
                const next = Number.parseInt(e.target.value, 10);
                setDraft({
                  ...draft,
                  selectionLimit: Number.isNaN(next) ? 1 : next,
                });
              }}
            />
          </FormField>
        )}
        {showGroupingUI && (
          <Checkbox
            checked={groupingDraft}
            onChange={(e) => setGroupingDraft(e.target.checked)}
          >
            Group Parameter
          </Checkbox>
        )}
      </form>
    </Dialog>
  );
}
